import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import Partners from './pages/Partners';
import Services from './pages/Services';
import SplashScreen from './components/SplashScreen';
import Presentation from './pages/Presentation'; // Import the new Presentation component
import Cemeteries from './pages/Cemeteries';
import CemeteryDetail from './pages/CemeteryDetail';
import AdminCemeteries from './pages/AdminCemeteries';
import NotFound from './pages/NotFound';

// The splash is a first-impression, not a page transition. In-app navigation
// never remounts App, so it already stays out of the way there — but a refresh
// or a deep link (someone opening /cemeteries directly) does remount, and
// without this the splash would replay every time. sessionStorage scopes it to
// one play per visit: a returning visitor still gets it, mid-visit reloads
// don't.
const SPLASH_SEEN_KEY = 'sc_splash_seen';

const hasSeenSplash = () => {
  try {
    return window.sessionStorage.getItem(SPLASH_SEEN_KEY) === '1';
  } catch {
    // Storage blocked (private browsing) — fall back to showing it.
    return false;
  }
};

const markSplashSeen = () => {
  try {
    window.sessionStorage.setItem(SPLASH_SEEN_KEY, '1');
  } catch {
    // Nothing to do; worst case the splash plays again.
  }
};

const App = () => {
  // Check if we're on the presentation route to skip the splash screen
  const isPresentation = window.location.pathname === '/presi';
  // Staff shouldn't sit through the splash every time they open the console.
  const isAdmin = window.location.pathname.startsWith('/admin');
  const skipSplash = isPresentation || isAdmin || hasSeenSplash();

  const [showSplash, setShowSplash] = useState(!skipSplash);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    if (skipSplash) return undefined;

    // Record it up front, so reloading mid-animation doesn't start it over.
    markSplashSeen();

    const timer = setTimeout(() => {
      setFadeOut(true);
      setTimeout(() => {
        setShowSplash(false);
      }, 1000);
    }, 5500);

    return () => clearTimeout(timer);
  }, [skipSplash]);

  return (
    <Router>
      <div className="relative min-h-screen flex flex-col bg-slate-50">
        {showSplash ? (
          <SplashScreen fadeOut={fadeOut} />
        ) : (
          <>
            {/* Only hide navbar and footer on presentation route */}
            {!isPresentation && <Navbar />}
            <main className="flex-grow">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<Services />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/presi" element={<Presentation />} />
                <Route path="/cemeteries" element={<Cemeteries />} />
                <Route path="/cemeteries/:cemeteryId" element={<CemeteryDetail />} />
                <Route path="/admin/cemeteries" element={<AdminCemeteries />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            {!isPresentation && <Footer />}
          </>
        )}
      </div>
    </Router>
  );
};

export default App;